import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { changePassword } from "../api/auth.js";
import { useApiClient } from "../api/ApiClientContext.js";
import { describeApiError } from "../domain/errors.js";
import type { AccountsPageLocationState } from "./AccountsPage.js";
import { useAuth } from "./AuthContext.js";
import "./login.css";

const MIN_PASSWORD_LENGTH = 8;

/**
 * Blocks the app behind a password-change form whenever the signed-in
 * account has `mustChangePassword` set — a brand-new account (an admin
 * knows the password they just typed for it) or the bootstrap admin (the
 * shop owner knows `ADMIN_PASSWORD`, see `auth/bootstrap.ts`). Rendered by
 * `AppShell` in place of the routed page, not as a separate route: there is
 * nowhere in the app a must-change account should be able to navigate to
 * before clearing this, and gating it at the shell means every protected
 * route is covered without `AppRoutes.tsx` needing a route per page to
 * remember to add the check to.
 */
export function ForcedPasswordChange() {
  const client = useApiClient();
  const auth = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Novo geslo mora imeti vsaj ${MIN_PASSWORD_LENGTH} znakov.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Gesli se ne ujemata.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await changePassword(client, currentPassword, newPassword);
      auth.applyAccountUpdate(res.account);
      // Clearing mustChangePassword drops this gate and reveals the app. An admin
      // still has no counter code yet (accounts and employee PINs are separate),
      // so send them straight to the user-admin screen to give themselves one —
      // the "prompt to set a code" after the first sign-in. A staff account
      // can't manage codes (an admin sets theirs), so it just lands on the list.
      if (res.account.role === "admin") {
        navigate("/accounts", { state: { promptSetCode: true } satisfies AccountsPageLocationState });
      }
    } catch (err) {
      setError(describeApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={(e) => void handleSubmit(e)}>
        <h1>Zamenjava gesla</h1>
        <p className="login-notice">Pred nadaljevanjem morate nastaviti novo geslo.</p>
        <label htmlFor="change-current">Trenutno geslo</label>
        <input
          id="change-current"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          autoFocus
          required
          disabled={submitting}
        />
        <label htmlFor="change-new">Novo geslo</label>
        <input
          id="change-new"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
          disabled={submitting}
        />
        <label htmlFor="change-confirm">Potrdite novo geslo</label>
        <input
          id="change-confirm"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
          disabled={submitting}
        />
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={submitting}>
          {submitting ? "Shranjevanje …" : "Nastavi geslo"}
        </button>
      </form>
    </main>
  );
}
